import { useCallback } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { useCreateSession, usePaySession } from '@/services';
import {
	clearSchedulingPayDraftSilently,
	executeSchedulingPayAfterAuth,
	isSchedulingPayAfterAuthPending,
	navigateToAgendamentoStripeCheckoutStep,
} from '@/utils';
import { toast } from 'react-toastify';

/**
 * Após login/cadastro com rascunho de agendamento: cria sessão e abre Stripe.
 * Retorna true se havia pendência e o fluxo foi iniciado.
 */
export function useResumeSchedulingPayAfterAuth() {
	const { mutateAsync: createSession } = useCreateSession();
	const { mutateAsync: paySession } = usePaySession();

	const resumeSchedulingPayAfterAuth = useCallback(
		async (navigate: NavigateFunction): Promise<boolean> => {
			if (!isSchedulingPayAfterAuthPending()) {
				return false;
			}

			const result = await executeSchedulingPayAfterAuth({
				createSession,
				paySession,
			});

			if (result.ok && result.redirected) {
				return true;
			}

			if (result.ok && !result.redirected) {
				navigateToAgendamentoStripeCheckoutStep(navigate);
				return true;
			}

			// Rascunho inválido/expirado/erro: não sequestrar login → /formulario ou destino do usuário
			if (result.reason === 'expired') {
				toast.info(
					'O horário reservado não está mais disponível. Escolha um novo horário para agendar.',
				);
			} else if (result.reason === 'error') {
				toast.error(
					'Não foi possível retomar o pagamento. Você pode agendar de novo quando quiser.',
				);
			}
			clearSchedulingPayDraftSilently();
			return false;
		},
		[createSession, paySession],
	);

	return {
		resumeSchedulingPayAfterAuth,
		isSchedulingPayPending: isSchedulingPayAfterAuthPending,
	};
}
